import React from 'react';
import { Alert, Stack, Button, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, InputLabel, MenuItem, Select, TextField } from "@mui/material"
import LinearProgress from '@mui/material/LinearProgress';
import AuthService from '../services/auth-service'
import { ECU_PREFIXES, firmwareChoices, validateAssignment, requestError } from './ecuFwResellerValidation'
import ResellerSearchList from './ResellerSearchList'

// ************************************************************

export default function EcuFwResellerFormDialog(props) {
  const [catalog, setCatalog] = React.useState({ rows: [], loading: false, error: "" })
  const [validationError, setValidationError] = React.useState("")
  const [reload, setReload] = React.useState(0)
  const [resellerSearchOpen, setResellerSearchOpen] = React.useState(false)
   
  const [formData, setFormData] = React.useState({
    ecu_prefix: '',
    country_code: '',
    reseller_id: -1,
    fw_name: '',
    note: '',
  })

  React.useEffect(() => {
    setValidationError("")
    setResellerSearchOpen(false)
    if (props.editRow) {
      setFormData({
        ecu_prefix: props.editRow.ecu_prefix || '',
        country_code: props.editRow.country_code || '',
        reseller_id: props.editRow.reseller_id !== undefined ? props.editRow.reseller_id : -1,
        fw_name: props.editRow.fw_name || '',
        note: props.editRow.note || '',
      })
    } else {
      setFormData({
        ecu_prefix: '',
        country_code: '',
        reseller_id: -1,
        fw_name: '',
        note: '',
      })
    }
  }, [props.editRow, props.open])

  React.useEffect(() => {
    if (!props.open) return
    let active = true
    setCatalog({ rows: [], loading: true, error: "" })
    Promise.all([AuthService.getFwResellerCatalog(), AuthService.getFwResellerCatalog(true)])
      .then(([release, preRelease]) => {
        const rows = firmwareChoices(release, preRelease)
        if (active) setCatalog({ rows, loading: false, error: "" })
      })
      .catch(error => {
        if (active) setCatalog({ rows: [], loading: false, error: requestError(error, "Firmware catalog unavailable") })
      })
    return () => { active = false }
  }, [props.open, reload])

  const handleClose = () => {
    props.onClickClose()
  }
  const handleSubmit = () => {
    const data = { ...formData, country_code: formData.country_code.trim().toUpperCase(), fw_name: formData.fw_name.trim(), note: formData.note.trim() }
    const error = validateAssignment(data, catalog.rows, props.resellersRows || [])
    setValidationError(error)
    if (error || catalog.loading || catalog.error) return
    if (props.editRow) {
      props.onClickSubmit({ id: props.editRow.id, ...data })
    } else {
      props.onClickSubmit(data)
    }
  }
  const handleTextBoxChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }))
  }  
  const handleSelectChange = (name) => (e) => {
    setFormData(prev => ({ ...prev, [name]: e.target.value }))
  }

  var view_en = {
    cancel_button: true,
    submit_button: true,
    show_spin: false,
  }  
  if (props.spin) {
    view_en.cancel_button = false
    view_en.submit_button = false
    view_en.show_spin = true
  }
    
  const isEdit = !!props.editRow
  const compatible = catalog.rows.filter(row => row.name.startsWith(formData.ecu_prefix))
  const selectedFirmware = catalog.rows.find(row => row.name === formData.fw_name)
  const selectedReseller = (props.resellersRows || []).find(row => row.id === formData.reseller_id)
  const resellerLabel = formData.reseller_id === -1 ? 'ANY (country required)' :
    selectedReseller ? selectedReseller.view_text || selectedReseller.company_name || selectedReseller.username :
      `Reseller ${formData.reseller_id} (unavailable)`

  return (    
    <div>      
      <Dialog 
        maxWidth='sm'
        fullWidth={true}
        open={props.open} 
      >
        <DialogTitle>{isEdit ? 'EDIT FW RESELLER' : 'ADD FW RESELLER'}</DialogTitle>
        <DialogContent>
          <Stack spacing={3}>
              {validationError && <Alert severity="error">{validationError}</Alert>}
              {catalog.error && <Alert severity="error" action={<Button onClick={() => setReload(value => value + 1)}>Retry</Button>}>{catalog.error}</Alert>}
              {catalog.loading && <LinearProgress />}
              <FormControl fullWidth margin="dense" variant="standard" disabled={view_en.show_spin}>
                <InputLabel id="fw-reseller-ecu-label">ECU Prefix *</InputLabel>
                <Select
                  labelId="fw-reseller-ecu-label"
                  value={formData.ecu_prefix}
                  onChange={handleSelectChange('ecu_prefix')}
                >
                  {ECU_PREFIXES.map(p => (
                    <MenuItem key={p} value={p}>{p}</MenuItem>
                  ))}
                  {formData.ecu_prefix && !ECU_PREFIXES.includes(formData.ecu_prefix) &&
                    <MenuItem value={formData.ecu_prefix} disabled>{formData.ecu_prefix} (unsupported)</MenuItem>}
                </Select>
              </FormControl>
              <TextField
                value={formData.country_code}
                margin="dense"
                name="country_code"
                label="Country Code (e.g. ITA, DEU - empty = any)"
                onChange={handleTextBoxChange}
                type="text"
                fullWidth
                variant="standard"
                disabled={view_en.show_spin}
                />
              <Stack spacing={1}>
                <TextField
                  label="Reseller (-1 = any)"
                  value={resellerLabel}
                  variant="standard"
                  fullWidth
                  InputProps={{ readOnly: true }}
                  disabled={view_en.show_spin}
                />
                <Button onClick={() => setResellerSearchOpen(true)} disabled={view_en.show_spin}
                  sx={{ alignSelf: 'flex-start' }}>Search reseller</Button>
              </Stack>
              <FormControl fullWidth margin="dense" variant="standard" disabled={view_en.show_spin || catalog.loading || !!catalog.error}>
                <InputLabel id="fw-reseller-firmware-label">Published Firmware *</InputLabel>
                <Select labelId="fw-reseller-firmware-label" value={formData.fw_name} onChange={handleSelectChange('fw_name')}>
                  {compatible.map(row => <MenuItem key={row.name} value={row.name}>{row.name} ({row.channels.join(", ")})</MenuItem>)}
                  {formData.fw_name && !compatible.some(row => row.name === formData.fw_name) &&
                    <MenuItem value={formData.fw_name} disabled>{formData.fw_name} (not published or incompatible)</MenuItem>}
                </Select>
              </FormControl>
              {formData.fw_name && <Alert severity={selectedFirmware && formData.fw_name.startsWith(formData.ecu_prefix) ? "info" : "warning"}>
                {selectedFirmware ? `Published: ${selectedFirmware.channels.join(", ")}; ${selectedFirmware.length} bytes` : "Firmware not available in the catalogs"}
              </Alert>}
              <TextField
                value={formData.note}
                margin="dense"
                name="note"
                label="Note"
                onChange={handleTextBoxChange}
                type="text"
                fullWidth
                variant="standard"
                disabled={view_en.show_spin}
                />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button disabled={!view_en.cancel_button} onClick={handleClose}>Cancel</Button>
          <Button disabled={!view_en.submit_button || catalog.loading || !!catalog.error} onClick={handleSubmit}>{isEdit ? 'Update' : 'Create'}</Button>
        </DialogActions>
        {view_en.show_spin && <LinearProgress />}
      </Dialog>
      <Dialog open={props.open && resellerSearchOpen} onClose={() => setResellerSearchOpen(false)} maxWidth="xl" fullWidth>
        <DialogTitle>SELECT RESELLER</DialogTitle>
        <DialogContent>
          <ResellerSearchList
            rows={props.resellersRows || []}
            selectedId={formData.reseller_id}
            allowAny
            disabled={view_en.show_spin}
            onSelect={id => {
              setFormData(prev => ({ ...prev, reseller_id: id }))
              setResellerSearchOpen(false)
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResellerSearchOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
